import Service from 'react-reservice';

import { ESymbolCategory, ISymbol } from '@aqx-core/common/api/market';
import { IOrder } from '@aqx-core/common/api/order';
import { ISignal } from '@aqx-core/common/api/signal';
import { EMarketComponentItems } from '@aqx-core/common/utils/market/types';

import { ISymbolPriceUpdate } from './type';

export interface IMarketServiceContext {
  topPickLoading?: boolean;
  topGainerLoading?: boolean;
  topLoserLoading?: boolean;
  myPositionLoading?: boolean;
  myFavouriteLoading?: boolean;
  watchlistLoading?: boolean;
  signalLoading?: boolean;

  // List of IDs of all symbols retrieved from API
  topPickSymbolIds: string[];
  topGainerSymbolIds: string[];
  topLoserSymbolIds: string[];
  myPositionSymbolIds: string[];
  myPositionOrdersIds: string[];
  myFavouriteSymbolIds: string[];
  watchlistSymbolIds: string[];
  signalSymbolIds: string[];

  // Total count before any slicing (needed for "View More" button logic)
  totalSignalCount: number;

  // List of IDs of symbols displayed on Market table according to selected SymbolCategory filter
  filteredTopPickSymbolIds: string[];
  filteredTopGainerSymbolIds: string[];
  filteredTopLoserSymbolIds: string[];
  filteredMyPositionSymbolIdsFromMappedSymbol: string[];
  filteredMyPositionOrderIdsFromMappedOrder: string[];
  filteredMyFavouriteSymbolIds: string[];
  filteredWatchlistSymbolIds: string[];
  filteredSignalSymbolIds: string[];

  // All symbols retrieved from API
  mappedTopPickSymbols: Record<string, ISymbol>;
  mappedTopGainerSymbols: Record<string, ISymbol>;
  mappedTopLoserSymbols: Record<string, ISymbol>;
  mappedMyFavouriteSymbols: Record<string, ISymbol>;
  mappedWatchlistSymbols: Record<string, ISymbol>;
  mappedMyPositionSymbols: Record<string, IOrder & { symbolType: ESymbolCategory } & Partial<ISymbolPriceUpdate>>;
  mappedMyPositionOrders: Record<string, IOrder & { symbolType: ESymbolCategory }>;
  mappedSignalSymbols: Record<string, ISignal>;

  selectedTopPickSymbolCategory: ESymbolCategory;
  selectedTopGainerSymbolCategory: ESymbolCategory;
  selectedTopLoserSymbolCategory: ESymbolCategory;
  selectedMyPositionSymbolCategory: ESymbolCategory;
  selectedMyFavouriteSymbolCategory: ESymbolCategory;
  selectedWatchListSymbolCategory: ESymbolCategory;
  selectedSignalSymbolCategory: ESymbolCategory;

  symbolCategories: ESymbolCategory[];

  // Mobile Market screen tab states
  marketMoverCurrentTab: string | undefined;
  watchlistCurrentTab: string | undefined;
  marketScreenTab: string | undefined;
}

const defaultContext: IMarketServiceContext = {
  topPickLoading: true,
  topGainerLoading: true,
  topLoserLoading: true,
  myPositionLoading: true,
  myFavouriteLoading: true,
  watchlistLoading: true,
  signalLoading: true,

  topPickSymbolIds: [],
  topGainerSymbolIds: [],
  topLoserSymbolIds: [],
  myPositionSymbolIds: [],
  myPositionOrdersIds: [],
  myFavouriteSymbolIds: [],
  watchlistSymbolIds: [],
  signalSymbolIds: [],

  totalSignalCount: 0,

  mappedTopPickSymbols: {},
  mappedTopGainerSymbols: {},
  mappedTopLoserSymbols: {},
  mappedMyPositionSymbols: {},
  mappedMyPositionOrders: {},
  mappedMyFavouriteSymbols: {},
  mappedWatchlistSymbols: {},
  mappedSignalSymbols: {},

  filteredTopPickSymbolIds: [],
  filteredTopGainerSymbolIds: [],
  filteredTopLoserSymbolIds: [],
  filteredMyPositionSymbolIdsFromMappedSymbol: [],
  filteredMyPositionOrderIdsFromMappedOrder: [],
  filteredMyFavouriteSymbolIds: [],
  filteredWatchlistSymbolIds: [],
  filteredSignalSymbolIds: [],

  selectedTopPickSymbolCategory: ESymbolCategory.ALL,
  selectedTopGainerSymbolCategory: ESymbolCategory.ALL,
  selectedTopLoserSymbolCategory: ESymbolCategory.ALL,
  selectedMyPositionSymbolCategory: ESymbolCategory.ALL,
  selectedMyFavouriteSymbolCategory: ESymbolCategory.ALL,
  selectedWatchListSymbolCategory: ESymbolCategory.ALL,
  selectedSignalSymbolCategory: ESymbolCategory.ALL,

  symbolCategories: [],

  // Mobile Market screen tab states
  marketMoverCurrentTab: undefined,
  watchlistCurrentTab: undefined,
  marketScreenTab: undefined
};

class MarketService extends Service<IMarketServiceContext> {
  constructor() {
    super();

    this.context = defaultContext;
  }

  setLoading = (filter: EMarketComponentItems, values: boolean) => {
    switch (filter) {
      case EMarketComponentItems.TOP_PICK:
        this.context.topPickLoading = values;
        break;
      case EMarketComponentItems.TOP_GAINER:
        this.context.topGainerLoading = values;
        break;
      case EMarketComponentItems.TOP_LOSER:
        this.context.topLoserLoading = values;
        break;
      case EMarketComponentItems.FAVOURITES:
        this.context.myFavouriteLoading = values;
        break;
      case EMarketComponentItems.ALL:
        this.context.watchlistLoading = values;
        break;
      case EMarketComponentItems.POSITIONS:
        this.context.myPositionLoading = values;
        break;
      case EMarketComponentItems.SIGNALS:
        this.context.signalLoading = values;
        break;
      default:
        break;
    }
  };

  setMappedSymbols = (filter: EMarketComponentItems, values: Record<string, ISymbol>) => {
    switch (filter) {
      case EMarketComponentItems.TOP_PICK:
        this.context.mappedTopPickSymbols = values;
        break;
      case EMarketComponentItems.TOP_GAINER:
        this.context.mappedTopGainerSymbols = values;
        break;
      case EMarketComponentItems.TOP_LOSER:
        this.context.mappedTopLoserSymbols = values;
        break;
      case EMarketComponentItems.FAVOURITES:
        this.context.mappedMyFavouriteSymbols = values;
        break;
      case EMarketComponentItems.ALL:
        this.context.mappedWatchlistSymbols = values;
        break;
      default:
        break;
    }
  };

  setMappedSignals = (values: Record<string, ISignal>) => {
    this.context.mappedSignalSymbols = values;
  };

  filterSymbolsByCategory = (filter: EMarketComponentItems, symbolCategory: ESymbolCategory) => {
    switch (filter) {
      case EMarketComponentItems.TOP_PICK:
        this.context.filteredTopPickSymbolIds = Object.keys(
          Object.fromEntries(
            Object.entries(this.context.mappedTopPickSymbols).filter(([, value]: [string, ISymbol]) => {
              return value.type === symbolCategory;
            })
          )
        );
        break;
      case EMarketComponentItems.TOP_GAINER:
        this.context.filteredTopGainerSymbolIds = Object.keys(
          Object.fromEntries(
            Object.entries(this.context.mappedTopGainerSymbols).filter(([, value]: [string, ISymbol]) => {
              return value.type === symbolCategory;
            })
          )
        );
        break;
      case EMarketComponentItems.TOP_LOSER:
        this.context.filteredTopLoserSymbolIds = Object.keys(
          Object.fromEntries(
            Object.entries(this.context.mappedTopLoserSymbols).filter(([, value]: [string, ISymbol]) => {
              return value.type === symbolCategory;
            })
          )
        );
        break;
      case EMarketComponentItems.FAVOURITES:
        this.context.filteredMyFavouriteSymbolIds = Object.keys(
          Object.fromEntries(
            Object.entries(this.context.mappedMyFavouriteSymbols).filter(([, value]: [string, ISymbol]) => {
              return value.type === symbolCategory;
            })
          )
        );

        break;
      case EMarketComponentItems.ALL:
        this.context.filteredWatchlistSymbolIds = Object.keys(
          Object.fromEntries(
            Object.entries(this.context.mappedWatchlistSymbols).filter(([, value]: [string, ISymbol]) => {
              return value.type === symbolCategory;
            })
          )
        );
        break;
      case EMarketComponentItems.SIGNALS:
        this.context.filteredSignalSymbolIds = Object.keys(
          Object.fromEntries(
            Object.entries(this.context.mappedSignalSymbols).filter(([, value]: [string, ISignal]) => {
              return value.type === symbolCategory;
            })
          )
        );
        break;
      // TODO: Need to check this
      case EMarketComponentItems.POSITIONS:
        this.context.filteredMyPositionSymbolIdsFromMappedSymbol = Object.keys(
          Object.fromEntries(
            Object.entries(this.context.mappedMyPositionSymbols).filter(([, value]: [string, IOrder]) => {
              return value.symbolType === symbolCategory;
            })
          )
        );
        this.context.filteredMyPositionOrderIdsFromMappedOrder = Object.entries(this.context.mappedMyPositionOrders)
          .filter(([, value]: [string, IOrder]) => {
            return value.symbolType === symbolCategory;
          })
          .map(([, value]) => value.orderId);

        break;
      default:
        break;
    }
  };

  setMappedOpenPositionsBySymbol = (values: Record<string, IOrder>) => {
    this.context.mappedMyPositionSymbols = values;
  };

  setMappedOpenPositionByOrderId = (values: Record<string, IOrder>) => {
    this.context.mappedMyPositionOrders = values;
  };

  setOrderIds = (values: string[]) => {
    this.context.myPositionOrdersIds = values;
  };

  setSymbolIds = (filter: EMarketComponentItems, values: string[]) => {
    switch (filter) {
      case EMarketComponentItems.TOP_PICK:
        this.context.topPickSymbolIds = values;
        this.context.filteredTopPickSymbolIds = values;
        break;
      case EMarketComponentItems.TOP_GAINER:
        this.context.topGainerSymbolIds = values;
        this.context.filteredTopGainerSymbolIds = values;
        break;
      case EMarketComponentItems.TOP_LOSER:
        this.context.topLoserSymbolIds = values;
        this.context.filteredTopLoserSymbolIds = values;
        break;
      case EMarketComponentItems.FAVOURITES:
        this.context.myFavouriteSymbolIds = values;
        this.context.filteredMyFavouriteSymbolIds = values;
        break;
      case EMarketComponentItems.ALL:
        this.context.watchlistSymbolIds = values;
        this.context.filteredWatchlistSymbolIds = values;
        break;
      case EMarketComponentItems.POSITIONS:
        this.context.myPositionSymbolIds = values;
        this.context.filteredMyPositionSymbolIdsFromMappedSymbol = values;
        break;
      case EMarketComponentItems.SIGNALS:
        this.context.signalSymbolIds = values;
        this.context.filteredSignalSymbolIds = values;
        break;
      default:
        break;
    }
  };

  setSignals = (symbols: Record<string, ISignal>, ids: string[]) => {
    this.context.mappedSignalSymbols = symbols;
    this.context.signalSymbolIds = ids;
    this.context.filteredSignalSymbolIds = ids;
  };

  setTotalSignalCount = (totalCount: number) => {
    this.context.totalSignalCount = totalCount;
  };

  setSelectedSymbolCategory = (filter: EMarketComponentItems, category: ESymbolCategory) => {
    switch (filter) {
      case EMarketComponentItems.TOP_PICK:
        this.context.selectedTopPickSymbolCategory = category;
        break;
      case EMarketComponentItems.TOP_GAINER:
        this.context.selectedTopGainerSymbolCategory = category;
        break;
      case EMarketComponentItems.TOP_LOSER:
        this.context.selectedTopLoserSymbolCategory = category;
        break;
      case EMarketComponentItems.FAVOURITES:
        this.context.selectedMyFavouriteSymbolCategory = category;
        break;
      case EMarketComponentItems.ALL:
        this.context.selectedWatchListSymbolCategory = category;
        break;
      case EMarketComponentItems.POSITIONS:
        this.context.selectedMyPositionSymbolCategory = category;

        break;
      case EMarketComponentItems.SIGNALS:
        this.context.selectedSignalSymbolCategory = category;
        break;
      default:
        break;
    }
  };

  setSymbolCategories = (values: ESymbolCategory[]) => {
    this.context.symbolCategories = values;
  };

  setMarketMoverCurrentTab = (value: string | undefined) => {
    this.context.marketMoverCurrentTab = value;
  };

  setWatchlistCurrentTab = (value: string | undefined) => {
    this.context.watchlistCurrentTab = value;
  };

  setMarketScreenTab = (value: string | undefined) => {
    this.context.marketScreenTab = value;
  };

  resetContext = () => {
    this.context = { ...defaultContext };
  };

  resetFilteredSymbols = (filter: EMarketComponentItems) => {
    switch (filter) {
      case EMarketComponentItems.TOP_PICK:
        this.context.filteredTopPickSymbolIds = Object.keys(this.context.mappedTopPickSymbols);
        break;
      case EMarketComponentItems.TOP_GAINER:
        this.context.filteredTopGainerSymbolIds = Object.keys(this.context.mappedTopGainerSymbols);
        break;
      case EMarketComponentItems.TOP_LOSER:
        this.context.filteredTopLoserSymbolIds = Object.keys(this.context.mappedTopLoserSymbols);
        break;
      case EMarketComponentItems.FAVOURITES:
        this.context.filteredMyFavouriteSymbolIds = Object.keys(this.context.mappedMyFavouriteSymbols);
        break;
      case EMarketComponentItems.ALL:
        this.context.filteredWatchlistSymbolIds = Object.keys(this.context.mappedWatchlistSymbols);
        break;
      case EMarketComponentItems.POSITIONS:
        this.context.filteredMyPositionSymbolIdsFromMappedSymbol = Object.keys(this.context.mappedMyPositionSymbols);
        this.context.filteredMyPositionOrderIdsFromMappedOrder = this.context.myPositionOrdersIds.map(
          (orderId) => this.context.mappedMyPositionOrders[orderId]?.orderId
        );

        break;
      case EMarketComponentItems.SIGNALS:
        this.context.filteredSignalSymbolIds = Object.keys(this.context.mappedSignalSymbols);
        break;
      default:
        break;
    }
  };
}

export const marketService = new MarketService();
